// @ts-nocheck
// Customers waiting in the team's queues, from the realtime feed's `queue`
// block: the four headline counters plus, per queue, each waiting call with
// a live wait timer derived from its enqueueTime.
import { queueWaitSeconds } from '@/utilities/queueWait';
import React, { useMemo } from 'react';
import { Clock, PhoneIncoming } from 'lucide-react';
import { buildQueues, clock } from './teamModel';
import { EmptyNote, HAIRLINE, LiveDot, Panel, TONE, TableShell, Td, Th, nf } from './teamUi';

function QueueStat({ label, value, tone }) {
  return (
    <div className={`rounded-xl border bg-muted/30 px-3 py-2 ${HAIRLINE}`}>
      <div className="truncate text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 truncate text-[16px] font-semibold leading-none tabular-nums ${tone ? TONE[tone].text : 'text-foreground'}`}>
        {value}
      </div>
    </div>
  );
}

function waitTone(seconds) {
  if (seconds >= 120) return 'crit';
  if (seconds >= 45) return 'warn';
  return 'good';
}

export default function TeamQueuePanel({ queue, now }) {
  const queues = useMemo(() => buildQueues(queue), [queue]);
  const waiting = Number(queue?.count) || 0;

  return (
    <Panel className="p-3.5">
      {waiting > 0 ? (
        <div className="mb-3 flex justify-end">
          <LiveDot tone="crit" label={`${waiting} waiting now`} />
        </div>
      ) : null}

      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <QueueStat label="Waiting" value={nf(waiting)} tone={waiting > 0 ? 'crit' : undefined} />
        <QueueStat label="Average wait" value={queue?.average ?? '—'} />
        <QueueStat label="Longest wait" value={queue?.maximum ?? '00:00:00'} />
        <QueueStat label="Max delay" value={queue?.maxDelay ?? '00:00:00'} />
      </div>

      {queues.length === 0 ? (
        <EmptyNote>
          <span className="inline-flex items-center gap-1.5">
            <PhoneIncoming size={13} />
            No calls are waiting in any call group
          </span>
        </EmptyNote>
      ) : (
        <div className="space-y-2.5">
          {queues.map((q) => (
            <div key={q.name} className={`overflow-hidden rounded-xl border ${HAIRLINE}`}>
              <div className={`flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2 ${HAIRLINE}`}>
                <span className="truncate text-[12px] font-semibold text-foreground">{q.name}</span>
                <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                  {q.calls.length} waiting
                </span>
              </div>
              <TableShell maxH={220}>
                <thead>
                  <tr>
                    <Th>Customer number</Th>
                    <Th align="right">Waiting for</Th>
                  </tr>
                </thead>
                <tbody>
                  {q.calls.map((call, idx) => {
                    const waited = queueWaitSeconds(call?.enqueueTime, now);
                    return (
                      <tr key={`${q.name}-${idx}`} className="transition-colors hover:bg-muted/40">
                        <Td className="tabular-nums">{call?.calleeTo || '—'}</Td>
                        <Td align="right">
                          {waited == null ? (
                            <span className="text-[11px] text-muted-foreground">—</span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums ${TONE[waitTone(waited)].text}`}>
                              <Clock size={11} />
                              {clock(waited)}
                            </span>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </TableShell>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
